/** BookingSyncForm — the embeddable launch half of Booking Sync: either
 *  filter by criteria (tours/external IDs + booking-start range +
 *  updated/created range, gated by the integration's `booking_query`
 *  capabilities) or provide explicit booking IDs (chips + .txt upload), set
 *  advanced options — then launch the sync task.
 *
 *  Hosted by BookingSyncPage (which owns the SyncScopePicker and injects it
 *  via `scopeSlot`) and by the tenant setup wizard (fixed scope,
 *  `onSubmit`/`onLaunched` injected so the wizard's preflight item owns the
 *  spawned task instead of navigating away). */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { useMutation, useQuery } from '@tanstack/react-query';
import { addDays, addMonths, addYears, endOfDay, format, subHours } from 'date-fns';
import { useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import * as integrationsApi from '@/api/integrations';
import type { ImportSource } from '@/api/integrations';
import * as opsApi from '@/api/ops';
import type { SyncBookingsStartInput } from '@/api/ops';
import * as syncApi from '@/api/sync';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { useSnackbar } from '@/components/Snackbar';
import { TimeWindowPicker } from '@/components/TimeWindowPicker';
import { ChipListField } from '@/features/tour-operators/integrations/wizard/ChipListField';

import { useIntegrationCatalog, type SyncScope } from './SyncScopePicker';

/** One selectable option = one external ID belonging to a tour. */
interface TargetOption {
  tourId: number;
  tourName: string;
  value: string;
  kind: 'primary' | 'ext';
  idType?: string;
}

const QUICK_RANGES: { label: string; compute: (from: Date) => Date }[] = [
  { label: '1 day', compute: (d) => addDays(d, 1) },
  { label: '3 days', compute: (d) => addDays(d, 3) },
  { label: '1 week', compute: (d) => addDays(d, 7) },
  { label: '2 weeks', compute: (d) => addDays(d, 14) },
  { label: '1 month', compute: (d) => addMonths(d, 1) },
  { label: '3 months', compute: (d) => addMonths(d, 3) },
  { label: '6 months', compute: (d) => addMonths(d, 6) },
  { label: '1 year', compute: (d) => addYears(d, 1) },
];

/** Presets for the updated/created range ("updated in the last N hours"). */
const UPDATED_PRESETS: { label: string; hours: number }[] = [
  { label: 'Last hour', hours: 1 },
  { label: 'Last 3h', hours: 3 },
  { label: 'Last 24h', hours: 24 },
  { label: 'Last 7 days', hours: 168 },
];

/** Above this many booking IDs the chip field collapses into a summary. */
const IDS_COLLAPSE_THRESHOLD = 100;

const isMidnight = (d: Date) => d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;

export interface BookingSyncFormProps {
  /** The tenant / integration / account this form queries and syncs for. */
  scope: SyncScope;
  /** Fixed-tenant hosts (the setup wizard) set this; the form itself never
   *  changes the scope, so today it is documentation for hosts. */
  lockScope?: boolean;
  /** The page injects its SyncScopePicker here so the original toolbar row
   *  (picker · targets spinner) survives extraction. */
  scopeSlot?: ReactNode;
  /** Launch override — the setup wizard routes the launch through its
   *  preflight item (stage + execute) instead of /ops/sync_bookings/start/. */
  onSubmit?: (input: SyncBookingsStartInput) => Promise<{ id: string }>;
  /** Where to go once launched. Default: navigate to the task's page. */
  onLaunched?: (taskId: string) => void;
  /** The ops page keeps its historical surveys-on default; a BACKFILL host
   *  passes false — old bookings must not trigger guest surveys by default. */
  defaultSendSurveys?: boolean;
}

export function BookingSyncForm({
  scope,
  scopeSlot,
  onSubmit,
  onLaunched,
  defaultSendSurveys = true,
}: BookingSyncFormProps) {
  const { show } = useSnackbar();
  const navigate = useNavigate();

  const [mode, setMode] = useState<'criteria' | 'ids'>('criteria');
  const [selected, setSelected] = useState<TargetOption[]>([]);
  const tomorrow = useMemo(() => addDays(new Date(), 1), []);
  const [fromDate, setFromDate] = useState<Date | null>(tomorrow);
  const [toDate, setToDate] = useState<Date | null>(addDays(tomorrow, 365));
  const [updatedFrom, setUpdatedFrom] = useState<Date | null>(null);
  const [updatedTo, setUpdatedTo] = useState<Date | null>(null);
  /** Which range drives the query when the integration can't combine both. */
  const [rangeMode, setRangeMode] = useState<'start' | 'updated'>('start');
  const [bookingIds, setBookingIds] = useState<string[]>([]);
  const [dryRun, setDryRun] = useState(false);
  // Advanced options
  const [source, setSource] = useState<ImportSource | null>(null);
  const [sendInvitations, setSendInvitations] = useState(false);
  const [sendSurveys, setSendSurveys] = useState(defaultSendSurveys);
  const [statuses, setStatuses] = useState('');
  const [reconcile, setReconcile] = useState(false);
  const [approval, setApproval] = useState(false);
  const [batchMultiplier, setBatchMultiplier] = useState(1);
  const [extraArgs, setExtraArgs] = useState<Record<string, string>>({});

  // A different tenant/integration/account invalidates the tour selection, the
  // updated range, the range mode and the staged extra args — the same resets
  // the page's picker onChange performed before extraction.
  const scopeKey = `${scope.tenant?.id ?? ''}|${scope.integration ?? ''}|${scope.account ?? ''}`;
  useEffect(() => {
    setSelected([]);
    setUpdatedFrom(null);
    setUpdatedTo(null);
    setRangeMode('start');
    setExtraArgs({});
  }, [scopeKey]);

  const scopeReady = Boolean(scope.tenant && scope.integration);

  // Capabilities of the selected integration (SYNC_BOOKINGS_QUERY).
  const catalog = useIntegrationCatalog();
  const bq = scope.integration ? catalog[scope.integration]?.booking_query : undefined;
  const startGran = bq?.start_range ?? 'date';
  const updGran = bq?.updated_range ?? null;
  const updatedLabel = bq?.updated_semantics === 'created' ? 'Created' : 'Updated';
  const canCombine = bq?.combined ?? false;
  const byIds = bq?.by_ids ?? true;

  const effMode = byIds ? mode : 'criteria';
  const startActive = effMode === 'criteria' && (!updGran || canCombine || rangeMode === 'start');
  const updatedActive =
    effMode === 'criteria' && updGran !== null && (canCombine || rangeMode === 'updated');

  const targets = useQuery({
    queryKey: ['sync-targets', scope.tenant?.id, scope.integration, scope.account],
    queryFn: () =>
      syncApi.fetchSyncTargets({
        tour_operator_id: scope.tenant!.id,
        integration: scope.integration!,
        account: scope.account,
      }),
    enabled: scopeReady,
  });

  const extraArgsQ = useQuery({
    queryKey: ['sync-extra-args', scope.tenant?.id, scope.integration, scope.account],
    queryFn: () =>
      syncApi.fetchSyncExtraArgs({
        tour_operator_id: scope.tenant!.id,
        integration: scope.integration!,
        account: scope.account,
      }),
    enabled: scopeReady,
  });
  const argDefs = useMemo(() => extraArgsQ.data?.extra_args ?? [], [extraArgsQ.data]);
  const missingRequiredArgs = argDefs.filter(
    (a) => a.required && !(extraArgs[a.name] ?? '').trim(),
  );

  const options = useMemo<TargetOption[]>(() => {
    const out: TargetOption[] = [];
    for (const t of targets.data?.results ?? []) {
      if (t.primary) {
        out.push({ tourId: t.tour.id, tourName: t.tour.name, value: t.primary, kind: 'primary' });
      }
      for (const e of t.ext_ids) {
        out.push({
          tourId: t.tour.id,
          tourName: t.tour.name,
          value: e.value,
          kind: 'ext',
          idType: e.id_type,
        });
      }
    }
    return out;
  }, [targets.data]);

  /** Booking-start bound → wire format. Date-granularity integrations get
   *  plain dates; datetime-granularity get UTC ISO, with an untouched (00:00)
   *  "to" time expanded to end-of-day per the range convention. */
  const fmtStart = (d: Date, isEnd: boolean): string => {
    if (startGran === 'date') return format(d, 'yyyy-MM-dd');
    const eff = isEnd && isMidnight(d) ? endOfDay(d) : d;
    return eff.toISOString();
  };
  const fmtUpdated = (d: Date): string =>
    updGran === 'date' ? format(d, 'yyyy-MM-dd') : d.toISOString();

  const startMut = useMutation({
    mutationFn: () => {
      const cleanArgs: Record<string, string> = {};
      for (const a of argDefs) {
        const v = (extraArgs[a.name] ?? '').trim();
        if (v) cleanArgs[a.name] = v;
      }
      return (onSubmit ?? opsApi.startSyncBookings)({
        tour_operator_id: scope.tenant!.id,
        integration_name: scope.integration!,
        account: scope.account,
        ...(effMode === 'ids'
          ? { booking_ids: bookingIds }
          : {
              ...(startActive && fromDate && toDate
                ? { from_date: fmtStart(fromDate, false), to_date: fmtStart(toDate, true) }
                : {}),
              ...(updatedActive && updatedFrom
                ? {
                    updated_from: fmtUpdated(updatedFrom),
                    ...(updatedTo ? { updated_to: fmtUpdated(updatedTo) } : {}),
                  }
                : {}),
              ...(selected.length ? { product_ids: selected.map((o) => o.value) } : {}),
            }),
        dry_run: dryRun,
        approval_required: !dryRun && approval,
        ...(batchMultiplier !== 1 ? { batch_multiplier: batchMultiplier } : {}),
        ...(Object.keys(cleanArgs).length ? { extra_args: cleanArgs } : {}),
        source_id: source?.code ?? undefined,
        send_invitations: sendInvitations,
        send_surveys: sendSurveys,
        statuses: statuses.trim()
          ? statuses
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : undefined,
        reconcile_existing: reconcile,
      });
    },
    onSuccess: (r) => {
      show(dryRun ? 'Dry run (fetch only) started.' : 'Booking sync started.', 'success');
      if (onLaunched) onLaunched(r.id);
      else navigate(`/bgtasks/${r.id}`);
    },
    onError: (e) => show((e as Error).message, 'error'),
  });

  /** Group toggle: select/deselect every ID of a tour. */
  const toggleTour = (tourId: number) => {
    const tourOptions = options.filter((o) => o.tourId === tourId);
    const allIn = tourOptions.every((o) => selected.some((s) => s.value === o.value));
    setSelected((prev) =>
      allIn
        ? prev.filter((s) => s.tourId !== tourId)
        : [...prev.filter((s) => s.tourId !== tourId), ...tourOptions],
    );
  };

  /** Merge a text file (one booking ID per line) into the chips, deduped. */
  const handleIdsFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading the same file
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const lines = String(reader.result ?? '')
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (!lines.length) {
        show('The file contains no booking IDs.', 'warning');
        return;
      }
      setBookingIds((prev) => {
        const merged = Array.from(new Set([...prev, ...lines]));
        show(
          `Loaded ${merged.length - prev.length} new booking ID(s) from ${file.name}.`,
          'success',
        );
        return merged;
      });
    };
    reader.onerror = () => show(`Could not read ${file.name}.`, 'error');
    reader.readAsText(file);
  };

  const rangesOk =
    effMode === 'ids'
      ? bookingIds.length > 0
      : Boolean((startActive && fromDate && toDate) || (updatedActive && updatedFrom));

  const StartPicker = startGran === 'datetime' ? DateTimePicker : DatePicker;
  const UpdatedPicker = updGran === 'date' ? DatePicker : DateTimePicker;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
          {scopeSlot}
          {targets.isFetching && <CircularProgress size={20} />}
        </Box>

        {argDefs.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            {argDefs.map((a) =>
              a.type === 'select' ? (
                <TextField
                  key={a.name}
                  select
                  size="small"
                  required={a.required}
                  label={a.label}
                  value={extraArgs[a.name] ?? ''}
                  onChange={(e) => setExtraArgs((p) => ({ ...p, [a.name]: e.target.value }))}
                  sx={{ minWidth: 220 }}
                >
                  {(a.choices ?? []).map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  key={a.name}
                  size="small"
                  required={a.required}
                  label={a.label}
                  value={extraArgs[a.name] ?? ''}
                  onChange={(e) => setExtraArgs((p) => ({ ...p, [a.name]: e.target.value }))}
                  sx={{ minWidth: 220 }}
                />
              ),
            )}
          </Box>
        )}

        <Stack spacing={2} sx={{ maxWidth: 900 }}>
          {byIds && (
            <Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={effMode}
                onChange={(_, v) => v && setMode(v as 'criteria' | 'ids')}
              >
                <ToggleButton value="criteria">By criteria</ToggleButton>
                <ToggleButton value="ids">By booking IDs</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {effMode === 'ids' ? (
            <Stack spacing={1.5}>
              {bookingIds.length > IDS_COLLAPSE_THRESHOLD ? (
                <Alert
                  severity="info"
                  action={
                    <Button color="inherit" size="small" onClick={() => setBookingIds([])}>
                      Clear
                    </Button>
                  }
                >
                  {bookingIds.length} booking IDs loaded.
                </Alert>
              ) : (
                <ChipListField
                  label="Booking IDs"
                  values={bookingIds}
                  onChange={setBookingIds}
                  placeholder="Type an ID and press Enter"
                  helperText="Each ID becomes a badge. Ranges and product filters are not used in this mode."
                />
              )}
              <Box>
                <Button
                  component="label"
                  size="small"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                >
                  Upload file (one ID per line)
                  <input
                    hidden
                    type="file"
                    accept=".txt,.csv,text/plain"
                    onChange={handleIdsFile}
                  />
                </Button>
              </Box>
            </Stack>
          ) : (
            <>
              <Autocomplete<TargetOption, true>
                multiple
                options={options}
                value={selected}
                onChange={(_, v) => setSelected(v)}
                disabled={!scopeReady || targets.isLoading}
                groupBy={(o) => `${o.tourName} [#${o.tourId}]`}
                getOptionLabel={(o) => o.value}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                filterOptions={(opts, state) => {
                  const q = state.inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter(
                    (o) =>
                      o.tourName.toLowerCase().includes(q) ||
                      String(o.tourId) === q ||
                      o.value.toLowerCase().includes(q),
                  );
                }}
                renderGroup={(params) => {
                  const groupOptions = options.filter(
                    (o) => `${o.tourName} [#${o.tourId}]` === params.group,
                  );
                  const tourId = groupOptions[0]?.tourId;
                  const inSelection = groupOptions.filter((o) =>
                    selected.some((s) => s.value === o.value),
                  ).length;
                  return (
                    <li key={params.key}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                          py: 0.25,
                          position: 'sticky',
                          top: -8,
                          bgcolor: 'background.paper',
                          zIndex: 1,
                          borderBottom: 1,
                          borderColor: 'divider',
                          cursor: 'pointer',
                        }}
                        onClick={() => tourId !== undefined && toggleTour(tourId)}
                      >
                        <Checkbox
                          size="small"
                          checked={inSelection > 0 && inSelection === groupOptions.length}
                          indeterminate={inSelection > 0 && inSelection < groupOptions.length}
                          sx={{ py: 0.5 }}
                        />
                        <Typography variant="subtitle2">{params.group}</Typography>
                      </Box>
                      <Box component="ul" sx={{ p: 0 }}>
                        {params.children}
                      </Box>
                    </li>
                  );
                }}
                renderOption={(props, o) => (
                  <Box component="li" {...props} key={`${o.tourId}-${o.value}`} sx={{ pl: 4 }}>
                    <Checkbox
                      size="small"
                      checked={selected.some((s) => s.value === o.value)}
                      sx={{ py: 0, mr: 1 }}
                    />
                    <code>{o.value}</code>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={o.kind === 'primary' ? 'primary' : o.idType}
                      sx={{ ml: 1 }}
                    />
                  </Box>
                )}
                renderTags={(vals, getTagProps) =>
                  vals.map((o, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={`${o.tourId}-${o.value}`}
                      size="small"
                      label={`${o.value} (${o.tourName})`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tours / external IDs"
                    placeholder={
                      scopeReady
                        ? 'Search by tour name, tour ID or external ID'
                        : 'Choose tenant + integration first'
                    }
                    helperText={
                      scopeReady && !targets.isLoading
                        ? `${options.length} selectable IDs across ${targets.data?.results.length ?? 0} tours — empty selection syncs all products`
                        : undefined
                    }
                  />
                )}
              />

              {updGran !== null && !canCombine && (
                <Box>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={rangeMode}
                    onChange={(_, v) => v && setRangeMode(v as 'start' | 'updated')}
                  >
                    <ToggleButton value="start">By start date</ToggleButton>
                    <ToggleButton value="updated">
                      By {updatedLabel.toLowerCase()} time
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                    {scope.integration} cannot combine both ranges in one query
                  </Typography>
                </Box>
              )}

              {startActive && (
                /* Top-aligned: helper text under the "To" field must not
                   push the sibling inputs off the shared baseline. */
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
                  <StartPicker
                    label="From"
                    value={fromDate}
                    onChange={setFromDate}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: startGran === 'datetime' ? 230 : 170 },
                      },
                    }}
                  />
                  <StartPicker
                    label="To"
                    value={toDate}
                    onChange={setToDate}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: startGran === 'datetime' ? 230 : 170 },
                        helperText: startGran === 'datetime' ? '00:00 → end of day' : undefined,
                      },
                    }}
                  />
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ alignItems: 'center', minHeight: 40 }}
                  >
                    {QUICK_RANGES.map((r) => (
                      <Chip
                        key={r.label}
                        size="small"
                        label={r.label}
                        variant="outlined"
                        onClick={() => fromDate && setToDate(r.compute(fromDate))}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {updatedActive && updGran !== 'date' && (
                /* Datetime-granularity integrations: one range widget with
                   relative presets replaces the two pickers + preset chips. */
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    alignItems: 'center',
                    minHeight: 40,
                  }}
                >
                  <TimeWindowPicker
                    keepRelative={false}
                    label={updatedLabel}
                    allowOpenEnded
                    value={[updatedFrom, updatedTo]}
                    onChange={([from, to]) => {
                      setUpdatedFrom(from);
                      setUpdatedTo(to);
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Empty &quot;to&quot; = up to now
                  </Typography>
                </Box>
              )}
              {updatedActive && updGran === 'date' && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
                  <UpdatedPicker
                    label={`${updatedLabel} from`}
                    value={updatedFrom}
                    onChange={setUpdatedFrom}
                    slotProps={{
                      textField: { size: 'small', sx: { width: 170 } },
                    }}
                  />
                  <UpdatedPicker
                    label={`${updatedLabel} to`}
                    value={updatedTo}
                    onChange={setUpdatedTo}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: 170 },
                        helperText: 'Empty = up to now',
                      },
                    }}
                  />
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ alignItems: 'center', minHeight: 40 }}
                  >
                    {UPDATED_PRESETS.map((r) => (
                      <Chip
                        key={r.label}
                        size="small"
                        label={r.label}
                        variant="outlined"
                        onClick={() => {
                          const now = new Date();
                          setUpdatedFrom(subHours(now, r.hours));
                          setUpdatedTo(now);
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          )}

          <FormControlLabel
            control={<Switch checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
            label="Dry run — fetch bookings + build XML artifacts, do not import"
          />

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Advanced options</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Box sx={{ width: 320 }}>
                  <RemoteAutocomplete<ImportSource>
                    label="Import source (source_id override)"
                    placeholder="Default from integration config"
                    size="small"
                    fetchOnEmpty
                    queryFn={(q, signal) =>
                      scope.tenant
                        ? integrationsApi.searchImportSources(scope.tenant.id, q, signal)
                        : Promise.resolve([])
                    }
                    getOptionLabel={(s) => `${s.name} (${s.code})`}
                    getOptionKey={(s) => s.id}
                    value={source}
                    onChange={(v) => setSource(v as ImportSource | null)}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sendInvitations}
                        onChange={(e) => setSendInvitations(e.target.checked)}
                      />
                    }
                    label="Send invitations"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sendSurveys}
                        onChange={(e) => setSendSurveys(e.target.checked)}
                      />
                    }
                    label="Send surveys"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={reconcile}
                        onChange={(e) => setReconcile(e.target.checked)}
                      />
                    }
                    label="Reconcile existing bookings"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={approval && !dryRun}
                        disabled={dryRun}
                        onChange={(e) => setApproval(e.target.checked)}
                      />
                    }
                    label="Review before import (approval step)"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    type="number"
                    label="Batch multiplier"
                    value={batchMultiplier}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      setBatchMultiplier(
                        Number.isFinite(n) ? Math.min(8, Math.max(1, Math.trunc(n))) : 1,
                      );
                    }}
                    helperText="Parallel import batches (1 = sequential)"
                    slotProps={{ htmlInput: { min: 1, max: 8 } }}
                    sx={{ width: 180 }}
                  />
                  <TextField
                    size="small"
                    label="Booking statuses"
                    placeholder="e.g. CONFIRMED, CANCELLED (comma-separated; empty = integration default)"
                    value={statuses}
                    onChange={(e) => setStatuses(e.target.value)}
                    sx={{ flexGrow: 1, minWidth: 320 }}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Box>
            <Button
              variant="contained"
              size="large"
              color={dryRun ? 'secondary' : 'primary'}
              disabled={
                !scopeReady || !rangesOk || missingRequiredArgs.length > 0 || startMut.isPending
              }
              onClick={() => startMut.mutate()}
            >
              {dryRun ? 'Start dry run' : 'Start booking sync'}
            </Button>
            {missingRequiredArgs.length > 0 && (
              <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>
                {missingRequiredArgs.map((a) => a.label).join(', ')} required
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
}
